import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import { createDeliverable } from 'sh-www/components/seller-postings/seller-posting-deliverables-input';

/**
 * List of constants for the various api calls in this file.
 *
 * @type {Object}
 */
export const BLAST_COMMAND_CONSTANTS = {
  BLAST_RESULTS_PARAMS: {
    DISTANCE: 'distance',         //required
    MIN_RATING: 'min_rating',     //required
    SELECT_STATUS: 'select',      // optional
    SERVICES: 'services',         //required
    SERVICES_SEARCH_TYPE: 'services_search_type', //optional
    EXPAND_TO_LIKE_SERVICES: 'expand_to_like_services', // optional
    COUNTRY_ID: 'country_ids',     // optional — TODO replace with 'country_id' when blaster is redone
    IS_COUNTRY_RESTRICTED: 'is_country_resricted', //optional
    PLACE_ID: 'place_id',         // optional
    LANGUAGES: 'languages',
    EQUIPMENT: 'equipment',
  }
};

/**
 * Service for admin blaster elated API calls.
 */
export default Service.extend({
  api: service(),
  store: service(),
  errorTracker: service('error-tracker'),
  shData: service(),

  /**
   * Send a admin blast for a particular assignment
   *
   * @param {Assignment} assignment
   * @param {Ember.A<FreelancerSearchResult>} freelancerSearchResults
   * @param {Object} params   With keys matching BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS
   * @param {String=} opt_customMessage     Optional message to send with the blast.
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  blastTheHunt(assignment, freelancerSearchResults, params, opt_customMessage = '', opt_includes = []) {
    let includes = opt_includes.join(','),
      selectedResults = [];

    freelancerSearchResults.forEach(freelancerSearchResult => {
      selectedResults.push(freelancerSearchResult.serialize());
    });

    let options = {
      jsonData: Object.assign({}, {
        /**
         * This is here because our backend code required a 'deliverables' field to validate that the assignment is
         * valid.  Rather than overly complicate the backend code, we'll leave this deliverables param to be sent
         * down to pass validation for our internal blast tool.
         * Validation requires 10 characters minimum.
         */
        'assignment_deliverables' : [ createDeliverable('no op - 10 character minimum') ],
        'selected-results' : selectedResults
      }, params),
      params: {
        include: includes
      }
    };

    if (opt_customMessage) {
      options.jsonData['message'] = opt_customMessage;
    }

    return new Promise((resolve, reject) => {
      this.get('api').post(`admin/postings/${assignment.get('id')}/blast`, options).then(response => {
        resolve(true);
      }).catch(e => {
        this.get('errorTracker').error('blastTheHunt -- error blasting the hunt', this, e);
        reject(e);
      });
    });
  },

  /**
   * Load the blast results for a given assignment.
   *
   * @param {Assignment} assignment
   * @param {Object} params   With keys matching BLAST_COMMAND_CONSTANTS.BLAST_RESULTS_PARAMS
   * @param {Array=} opt_includes   Optional includes
   * @return {Ember.RSVP.Promise}
   */
  loadBlastResults(assignment, params, opt_includes = []) {
    let includes = opt_includes.join(','),
      options = {
        jsonData : params,
        params: {
          include: includes
        }
      };

    return new Promise((resolve, reject) => {
      this.get('api').post(`admin/postings/${assignment.get('id')}/blast-results`, options).then(response => {
        this.shData.pushResponseToStore(response);
        let freelancerSearchResultCollection = this.get('store').peekRecord('freelancerSearchResultCollection', response.data.id);
        resolve(freelancerSearchResultCollection);
      }).catch(e => {
        this.get('errorTracker').error('loadBlastResults -- error getting blast results', this, e);
        reject(e);
      });
    });
  },

  /**
   * Load the blast results for a given assignment.
   *
   * @param {Assignment} assignment
   * @param {Object} params
   * @return {Ember.RSVP.Promise}
   */
  loadBlastHistory(assignment, params) {
    return this.api.request(`admin/postings/${assignment.id}/blast-history`, params);
  },
});
